import {
  faBitbucket,
  faGithub,
  faGitlab,
  faMicrosoft,
} from "@fortawesome/free-brands-svg-icons";
import {
  faBookBookmark,
  faCircleUser,
  faCodeBranch,
  faCodeCommit,
  faTag,
  faTicket,
} from "@fortawesome/pro-solid-svg-icons";

import { ScmType } from "@protos/common/v1/common";
import { Issue } from "@protos/issues/v1/issues";
import { formatProviderRef, getSanitizedUrl } from "@shared/utils";

import { InfoIcon } from "../../types/infoIcon";
import { getGitProviderLinks } from "../../utils";

const getProviderIcon = (provider: ScmType | undefined) => {
  switch (provider) {
    case ScmType.GITHUB:
    case ScmType.GITHUB_ENTERPRISE:
      return faGithub;
    case ScmType.GITLAB:
    case ScmType.GITLAB_SELFMANAGED:
      return faGitlab;
    case ScmType.BITBUCKET:
    case ScmType.BITBUCKET_DATACENTER:
      return faBitbucket;
    case ScmType.AZURE_DEVOPS:
      return faMicrosoft;
    default:
      return faBookBookmark;
  }
};

export function generateSastIcons(finding: Issue) {
  const { ref: refName, refUrl, provider } = getGitProviderLinks(finding);
  const repoUrl = getSanitizedUrl(finding.lastSeenScan?.meta?.repo_url);
  const providerIcon = getProviderIcon(provider);
  const formattedFindingBranches = finding.relatedIssues.map((branch) =>
    formatProviderRef(provider, branch.ref, parseInt(branch.pullRequestId))
  );
  const branchesCountText =
    finding.relatedIssues.length > 0 ? `+${finding.relatedIssues.length}` : "";

  const commitHash: string = finding.firstSeenScan?.meta?.commit ?? "";

  const iconList: InfoIcon[] = [];

  iconList.push({
    icon: faCircleUser,
    label: finding.firstSeenScan?.meta?.commit_author_name ?? "",
    onHoverText: [finding.firstSeenScan?.meta?.commit_author_email ?? ""],
  });
  if (finding.aiTags?.tags) {
    iconList.push({
      icon: faTag,
      label: finding.aiTags?.tags,
      onHoverText: [
        `Assistant thinks this file relates to ${finding.aiTags?.tags}`,
      ],
    });
  }

  iconList.push({
    icon: providerIcon,
    label: finding.repository?.name ?? "",
    href: repoUrl,
  });
  iconList.push({
    icon: faCodeBranch,
    label: `${refName} ${branchesCountText}`,
    href: refUrl,
    onHoverText: [refName],
    branches: formattedFindingBranches,
  });
  iconList.push({
    icon: faCodeCommit,
    label: commitHash.slice(0, 7),
    href: finding.commitUrl,
    copyText: commitHash,
    copyTooltipText: "Copy full commit ID",
  });
  if (finding.externalTicket) {
    iconList.push({
      icon: faTicket,
      label: finding.externalTicket.externalSlug,
      href: finding.externalTicket.url,
    });
  } else if (finding.ticketAttempts.length) {
    let errorMessage = finding.ticketAttempts[0].responseMessage;
    if (errorMessage.includes("Client Error")) {
      errorMessage =
        "400 Bad Request. Please check your configuration settings.";
    }
    iconList.push({
      icon: faTicket,
      label: "Failed",
      onHoverText: [`Ticket creation failed with message: ${errorMessage}`],
      color: "var(--mantine-color-red-7)",
    });
  }

  return iconList;
}
